/**
 * ZITELOU - Subscription Details Page
 * Page de détail d'un abonnement avec historique Stripe
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PauseCircleIcon,
  XCircleIcon,
  UserIcon,
    CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, Badge, Loader, Table, ConfirmModal } from '../components/Common';
import { useApi } from '../hooks';
import api from '../services/api';
import { formatDate, formatCurrency } from '../utils';
import { toast } from 'react-toastify';

const SubscriptionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading, execute } = useApi();
  const [subscription, setSubscription] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);

  const fetchSubscription = useCallback(async () => {
    await execute(async () => {
      const result = await api.getSubscriptionById(id);
      setSubscription(result);
    });
  }, [id, execute]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleSuspend = async () => {
    try {
      await api.suspendSubscription(id);
      toast.success('Abonnement suspendu');
      setShowSuspendModal(false);
      fetchSubscription();
    } catch (error) {
      toast.error('Erreur lors de la suspension');
    }
  };

  const handleCancel = async () => {
    try {
      await api.cancelSubscription(id);
      toast.success('Abonnement résilié');
      setShowCancelModal(false);
      fetchSubscription();
    } catch (error) {
      toast.error('Erreur lors de la résiliation');
    }
  };

    const handleActivate = async () => {
        try {
            await api.activateSubscription(id);
            toast.success('Abonnement activé');
            setShowActivateModal(false);
            fetchSubscription();
        } catch (error) {
            toast.error('Erreur lors de l\'activation');
        }
    };

  if (loading || !subscription) {
    return <Loader fullScreen text="Chargement de l'abonnement..." />;
  }

  // Transaction table columns
  const transactionColumns = [
    {
      key: 'date',
      label: 'Date',
      sortable: true,
      render: (value) => formatDate(value),
    },
    {
      key: 'amount',
      label: 'Montant',
      render: (value) => (
        <span className="font-semibold text-gray-900">{formatCurrency(value)}</span>
      ),
    },
    {
      key: 'status',
      label: 'Statut',
      render: (value) => (
        <Badge
          status={value === 'succeeded' ? 'success' : 'error'}
          size="sm"
        >
          {value === 'succeeded' ? 'Réussi' : 'Échoué'}
        </Badge>
      ),
    },
    {
      key: 'payment_method',
      label: 'Moyen de paiement',
      render: (value) => <span className="text-sm text-gray-600">{value}</span>,
    },
    {
      key: 'id',
      label: 'ID Transaction',
      render: (value) => (
        <span className="font-mono text-xs text-gray-500">{value.slice(0, 20)}...</span>
      ),
    },
  ];

  const successfulTransactions = subscription.transactions.filter(
    (t) => t.status === 'succeeded'
  );
  const failedTransactions = subscription.transactions.filter(
    (t) => t.status === 'failed'
  );
  const totalRevenue = successfulTransactions.reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<ArrowLeftIcon className="w-4 h-4" />}
            onClick={() => navigate('/subscriptions')}
          >
            Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Abonnement #{subscription.id}
            </h1>
            <p className="text-gray-600 mt-1">
              Parent : {subscription.user_name}
            </p>
          </div>
          <Badge status={subscription.status} dot />
        </div>

        <div className="flex items-center gap-2">
          {subscription.status === 'active' && (
            <>
              <Button
                variant="warning"
                leftIcon={<PauseCircleIcon className="w-4 h-4" />}
                onClick={() => setShowSuspendModal(true)}
              >
                Suspendre
              </Button>
              <Button
                variant="danger"
                leftIcon={<XCircleIcon className="w-4 h-4" />}
                onClick={() => setShowCancelModal(true)}
              >
                Résilier
              </Button>
            </>
          )}
            {subscription.status === 'suspended' && (
                <>
                    <Button
                        variant="success"
                        leftIcon={<CheckCircleIcon className="w-4 h-4" />}
                        onClick={() => setShowActivateModal(true)}
                    >
                        Activer
                    </Button>
                    <Button
                        variant="danger"
                        leftIcon={<XCircleIcon className="w-4 h-4" />}
                        onClick={() => setShowCancelModal(true)}
                    >
                        Résilier
                    </Button>
                </>
            )}
        </div>
      </div>

      {/* Subscription Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <Card title="Informations Générales" className="lg:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-500 mb-2 block">
                ID Stripe
              </label>
              <p className="font-mono text-sm text-gray-900 break-all">
                {subscription.stripe_id}
              </p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <UserIcon className="w-4 h-4" />
                Parent
              </label>
              {subscription.user ? (
                <Link
                  to={`/users/${subscription.user.id}`}
                  className="text-primary hover:opacity-80 font-medium"
                >
                  {subscription.user_name}
                </Link>
              ) : (
                <p className="text-gray-900">{subscription.user_name}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-2 block">
                Type
              </label>
              <p className="text-gray-900 capitalize">
                {subscription.type === 'monthly' ? 'Mensuel' : 'Annuel'}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-2 block">
                Montant
              </label>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(subscription.amount)}
                <span className="text-sm font-normal text-gray-500">
                  {' '}
                  / {subscription.type === 'monthly' ? 'mois' : 'an'}
                </span>
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-2 block">
                Date de début
              </label>
              <p className="text-gray-900">{formatDate(subscription.start_date)}</p>
            </div>

            {subscription.next_billing && (
              <div>
                <label className="text-sm font-medium text-gray-500 mb-2 block">
                  Prochaine facturation
                </label>
                <p className="text-gray-900 font-semibold">
                  {formatDate(subscription.next_billing)}
                </p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-500 mb-2 block">
                Statut
              </label>
              <Badge status={subscription.status} size="md" dot />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-2 block">
                Dernier paiement
              </label>
              <p className="text-gray-900">
                {subscription.last_payment
                  ? formatDate(subscription.last_payment)
                  : '-'}
              </p>
            </div>
          </div>
        </Card>

        {/* Transaction Stats */}
        <Card title="Statistiques">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">
                Total transactions
              </label>
              <p className="text-3xl font-bold text-gray-900">
                {subscription.transactions.length}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">
                Réussies
              </label>
              <p className="text-2xl font-bold text-success">
                {successfulTransactions.length}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">
                Échouées
              </label>
              <p className="text-2xl font-bold text-danger">
                {failedTransactions.length}
              </p>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <label className="text-sm font-medium text-gray-500 mb-1 block">
                Revenu total
              </label>
              <p className="text-2xl font-bold text-primary">
                {formatCurrency(totalRevenue)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Transaction History */}
      <Card
        title="Historique des Transactions Stripe"
        subtitle={`${subscription.transactions.length} transaction(s)`}
      >
        <Table
          columns={transactionColumns}
          data={subscription.transactions}
          keyField="id"
          striped
          hoverable
          emptyMessage="Aucune transaction trouvée"
        />
      </Card>

      {/* Suspend Modal */}
      <ConfirmModal
        isOpen={showSuspendModal}
        onClose={() => setShowSuspendModal(false)}
        onConfirm={handleSuspend}
        title="Suspendre l'abonnement"
        message={`Êtes-vous sûr de vouloir suspendre l'abonnement de ${subscription.user_name} ? Le parent ne pourra plus accéder aux fonctionnalités premium jusqu'à la réactivation.`}
        confirmText="Suspendre"
        confirmVariant="warning"
      />

        {/* Activate Modal */}
        <ConfirmModal
            isOpen={showActivateModal}
            onClose={() => setShowActivateModal(false)}
            onConfirm={handleActivate}
            title="Activer l'abonnement"
            message={`Êtes-vous sûr de vouloir réactiver l'abonnement de ${subscription.user_name} ? Le parent retrouvera l'accès aux fonctionnalités premium.`}
            confirmText="Activer"
            confirmVariant="success"
        />

      {/* Cancel Modal */}
      <ConfirmModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancel}
        title="Résilier l'abonnement"
        message={`Êtes-vous sûr de vouloir résilier l'abonnement de ${subscription.user_name} ? Cette action est irréversible et annulera tous les paiements futurs via Stripe.`}
        confirmText="Résilier définitivement"
        confirmVariant="danger"
      />
    </div>
  );
};

export default SubscriptionDetails;
